import { useEffect, useState, useRef } from 'react';
import FormField from '../common/FormField';
import FormSection from '../common/FormSection';
import Button from '../common/Button';
import { useMemberOtpApi } from '../../hooks/useMemberOtpApi';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { MemberDTO } from '../../services/apiClient';

interface OtpProps {
  member: MemberDTO | undefined;
  onNext: () => void;
}

interface Otp {
  otp: string;
}

const FORM_SECTIONS = {
  otpConfirmation: {
    id: 'otpConfirmation',
    title: 'OTP Confirmation',
  },
} as const;

const Otp: React.FC<OtpProps> = ({ member, onNext }) => {
  const [otp, setOtp] = useState<Otp>({ otp: '' });
  const [canGenerateOtp, setCanGenerateOtp] = useState(true);
  const [canGenerateEmailOtp, setCanGenerateEmailOtp] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [emailOtpVisible, setEmailOtpVisible] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const hasGeneratedInitialOtp = useRef(false);

  const { create, verify } = useMemberOtpApi();

  const EMAILOTP = 'email';
  const SMSOTP = 'sms';

  const validationRules: ValidationRules = {
    otp: [{ required: true, message: 'OTP is required' }],
  };
  const { errors, validate } = useFormValidation<Otp>(validationRules);

  const generateOtp = async () => {
    if (!canGenerateOtp) return;

    try {
      await create(SMSOTP);
      setCanGenerateOtp(false);
      setCountdown(60);

      setCanGenerateEmailOtp(false);
      setTimeout(() => {
        setCanGenerateEmailOtp(true);
      }, 60000);
    } catch (error) {
      console.error('Error generating member otp', error);
    }
  };

  const generateEmailOtp = async () => {
    if (!canGenerateEmailOtp) return;

    try {
      await create(EMAILOTP);
      setCanGenerateEmailOtp(false);
      setEmailCountdown(60);
    } catch (error) {
      console.error('Error generating email otp', error);
    }
  };

  useEffect(() => {
    if (!hasGeneratedInitialOtp.current) {
      hasGeneratedInitialOtp.current = true;
      generateOtp();
    }
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    let emailTimer: NodeJS.Timeout;

    // SMS countdown timer
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && !canGenerateOtp) {
      setCanGenerateOtp(true);
      setEmailOtpVisible(true);
    }

    // Email countdown timer
    if (emailCountdown > 0) {
      emailTimer = setTimeout(() => {
        setEmailCountdown(emailCountdown - 1);
      }, 1000);
    } else if (emailCountdown === 0 && !canGenerateEmailOtp) {
      setCanGenerateEmailOtp(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (emailTimer) clearTimeout(emailTimer);
    };
  }, [countdown, canGenerateOtp, emailCountdown, canGenerateEmailOtp]);

  const handleVerifyOtp = async () => {
    try {
      if (!validate(otp)) return;

      const response = await verify(otp.otp);
      if (response) {
        if (response.data === true) {
          onNext();
        } else {
          setVerificationError(response.message || '');
        }
      }
    } catch (error) {
      console.error('Error verifying otp', error);
      setVerificationError('Verification failed. Please try again.');
    }
  };

  const handleResendSmsOtp = () => {
    if (canGenerateOtp) {
      setOtp({ otp: '' }); // Clear the OTP
      setVerificationError(''); // Clear error
      generateOtp();
    }
  };

  const handleSendEmailOtp = () => {
    if (canGenerateEmailOtp) {
      setOtp({ otp: '' }); // Clear the OTP
      setVerificationError(''); // Clear error
      generateEmailOtp();
    }
  };

  const handleOtpChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const value = e.target.value;
    if (/^\d{0,6}$/.test(value)) {
      setOtp({ otp: value });
      setVerificationError(''); // Clear error when user types
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <FormSection
      id={FORM_SECTIONS.otpConfirmation.id}
      title={FORM_SECTIONS.otpConfirmation.title}
      columns={1}
    >
      <FormField
        id="otp"
        label="One time pin"
        type="text"
        value={otp.otp || ''} // Ensure it's always a string
        onChange={handleOtpChange}
        placeholder="Please enter the OTP code we sent to your phone"
        required={true}
        error={errors.otp || verificationError}
        helper={`SMS sent to ${member?.phone}, please copy the code in the SMS and enter above.`}
        maxLength={6}
      />
      <Button className="mr-3" onClick={handleVerifyOtp}>
        Continue
      </Button>
      <Button
        variant="secondary"
        className="mr-3"
        onClick={handleResendSmsOtp}
        disabled={!canGenerateOtp}
      >
        {canGenerateOtp
          ? 'Send Sms Otp'
          : `Resend in ${formatCountdown(countdown)}`}
      </Button>
      <Button
        className={emailOtpVisible ? 'visible' : 'invisible'}
        variant="secondary"
        onClick={handleSendEmailOtp}
      >
        {canGenerateEmailOtp
          ? 'Email OTP'
          : `Resend in ${formatCountdown(emailCountdown)}`}
      </Button>
    </FormSection>
  );
};

export default Otp;
